import { GoogleGenAI } from "@google/genai";
import { getDefaultModel } from "../config/llmProviders";
import {
  clampThinkingConfig,
  supportsGeminiThinking,
  supportsMinimalThinking,
} from "./geminiThinking";

// Re-exported so existing import sites keep pointing at this module; the
// rules themselves live in geminiThinking.js so they can be unit tested.
export { supportsGeminiThinking, supportsMinimalThinking };

const getApiKey = (settingsOrKey) => {
  if (typeof settingsOrKey === "string") {
    return settingsOrKey.trim();
  }

  return (settingsOrKey?.apiKey || settingsOrKey?.apiKeys?.google || "").trim();
};

const getModel = (settingsOrModel) => {
  if (typeof settingsOrModel === "string") {
    return settingsOrModel;
  }

  return settingsOrModel?.model || getDefaultModel("google");
};

// `httpOptions` points the SDK somewhere other than Google, which is how a
// call on a mate's shared credits reaches the API (see llm/cloudRoute.js).
const createClient = (apiKey, httpOptions) => {
  if (!apiKey) {
    throw new Error("Google API key is required");
  }

  return new GoogleGenAI(httpOptions ? { apiKey, httpOptions } : { apiKey });
};

const normalizeGeminiError = (error) => {
  if (!error) {
    return new Error("Gemini request failed");
  }

  if (error.name === "AbortError") {
    return error;
  }

  const message = error.message || error.statusText || "Gemini request failed";

  return new Error(`Google API Error: ${message}`);
};

export const generateGeminiText = async ({
  apiKey,
  httpOptions,
  model,
  contents,
  config,
  onUsage,
}) => {
  try {
    const resolvedModel = getModel(model);
    const client = createClient(getApiKey(apiKey), httpOptions);
    const resolvedConfig = clampThinkingConfig(resolvedModel, {
      ...(config || {}),
    });

    // Apply thinking config if the model supports it and the user hasn't overridden it
    if (
      supportsGeminiThinking(resolvedModel) &&
      resolvedConfig.thinkingConfig === undefined
    ) {
      resolvedConfig.thinkingConfig = {
        // /TODO: Adjust thinkingLevel based on your requirements (settings)
        thinkingLevel: "MEDIUM", // Options typically include: "MINIMAL", "LOW", "MEDIUM", "HIGH"
      };
    }

    const response = await client.models.generateContent({
      model: resolvedModel,
      contents,
      config: resolvedConfig,
    });

    // Token counts for cost accounting (issue #71). Reported before the
    // empty-response guard: those tokens were billed either way.
    if (onUsage && response?.usageMetadata) onUsage(response.usageMetadata);

    if (!response?.text) {
      throw new Error("Gemini returned an empty response");
    }

    return response.text;
  } catch (error) {
    throw normalizeGeminiError(error);
  }
};

export const generateGeminiTextStream = async ({
  apiKey,
  httpOptions,
  model,
  contents,
  config,
  onThinking,
  onUsage,
}) => {
  try {
    const resolvedModel = getModel(model);
    const client = createClient(getApiKey(apiKey), httpOptions);
    const resolvedConfig = clampThinkingConfig(resolvedModel, {
      ...(config || {}),
    });

    const stream = await client.models.generateContentStream({
      model: resolvedModel,
      contents,
      config: resolvedConfig,
    });

    let accumulatedThinking = "";
    let accumulatedText = "";
    // Streamed chunks carry a running usageMetadata; the last one seen is the
    // total for the request.
    let lastUsageMetadata = null;

    for await (const chunk of stream) {
      if (resolvedConfig.abortSignal?.aborted) {
        throw new DOMException("Aborted", "AbortError");
      }

      if (chunk.usageMetadata) lastUsageMetadata = chunk.usageMetadata;

      const parts = chunk.candidates?.[0]?.content?.parts;
      if (!parts) {
        console.debug("[GeminiStream] Chunk with no parts:", JSON.stringify(chunk).substring(0, 200));
        continue;
      }

      for (const part of parts) {
        console.debug("[GeminiStream] Part:", { thought: part.thought, hasText: !!part.text, textLen: part.text?.length });
        if (part.thought === true && part.text) {
          accumulatedThinking += part.text;
          if (onThinking) onThinking(accumulatedThinking);
        } else if (part.text) {
          accumulatedText += part.text;
        }
      }
    }

    console.debug("[GeminiStream] Done. Thinking:", accumulatedThinking.length, "Text:", accumulatedText.length);

    if (onUsage && lastUsageMetadata) onUsage(lastUsageMetadata);

    if (!accumulatedText) {
      throw new Error("Gemini streaming returned an empty response");
    }

    return accumulatedText;
  } catch (error) {
    throw normalizeGeminiError(error);
  }
};

export const testGeminiConnection = async ({ apiKey, model }) => {
  await generateGeminiText({
    apiKey,
    model,
    contents: "Reply with OK only.",
    config: {
      maxOutputTokens: 1024,

      // Keep thinking as low as the model allows for the connection test, to
      // save latency and tokens. Because this is not 'undefined', the wrapper
      // won't auto-apply MEDIUM, and clampThinkingConfig lifts it to LOW on
      // the models that reject MINIMAL outright.
      thinkingConfig: {
        thinkingLevel: "MINIMAL",
      },
    },
  });
};
